/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash.debounce';
import Features from 'security/Features';
import {globalAccountMapSelector} from '@ion/applications/src/store/selectors/global-group';
import {get} from '@ion/core/src/environment/environment';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {LatLong} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {notDeepEqual} from '@ion/core/src/utils/Equality';
import {
  GEOFENCE_LAYER
} from '@ion/dls/src/maps/layers/GeofenceLayerSettings';
import {
  SC_DEVICE_LAYER
} from '@ion/dls/src/maps/layers/SCDeviceLayerSettings';
import {
  TRAFFIC_LAYER
} from '@ion/dls/src/maps/layers/TrafficLayerSettings';
import {
  WEATHER_LAYER
} from '@ion/dls/src/maps/layers/WeatherLayerSettings';
import {
  BoundaryMode,
  HistoryInputEvents,
  HistoryReportTypeEnum
} from '@ion/dls/src/types/types';
import {TrackingMapLayer} from '@ion/ion-suite/src/types/preferences';
import {
  clearSelectedTrackingSearchResult,
  loadAssetHistory,
  onChangeAssetContent,
  onChangeDeviceLabelContent,
  resetAssetHistory,
  setAssetHistoryInputEvents,
  setAssetHistoryInterval,
  setAssetHistoryReportType,
  setAssetsHistoryUpdatesLive,
  setFleetLayerPersistenceActive,
  setGeofenceBorderOnly,
  setGeofenceLabelState,
  setGeofenceLayerOpacity,
  setGeoFencePanelOpen,
  setOperationalLayersOpacity,
  setOperationalLayerVisibility,
  setOperationalSublayerVisibility,
  setSavedOperationalLayers,
  setSelectedTrackingSearchResult,
  setTrackingMapLayers,
  setTrafficLayerOpacity,
  setTrafficLayerSubLayers,
  setWeatherLayerOpacity,
  showHistoryRefreshMessage,
  toggleAssetClustering,
  toggleAssetLabelling,
  toggleDeviceLabelling,
  toggleMapControls,
  updateLayers
} from '@/store/actions/tracking';
import {globalGroupSelector} from '@/store/selectors/global-group-chooser-selectors';
import {userSelector} from '@/store/selectors/login-selectors';
import {
  assetLabelContentSelector,
  assetsHistoryLoadingSelector,
  boundaryModeSelector,
  findNearbySelector,
  geofenceBorderOnlySelector,
  geofenceLabelSelector,
  geofenceOpacitySelector,
  geoFencePanelOpenSelector,
  geofenceStatusSelector,
  historyActiveSelector,
  historyInputEventsSelector,
  historyIntervalInHoursSelector,
  historyReportTypeSelector,
  isClusteringActiveSelector,
  isClusteringV2ActiveSelector,
  isLabellingActiveSelector,
  islayerVisibilitySelector,
  isScDevicesLabelActiveSelector,
  liveHistoryActiveSelector,
  liveHistoryHideNonSelectedAssetsActive,
  liveHistoryInitialAssetsSelector,
  liveHistoryIsFetchingSelector,
  liveHistoryIsUIFetchingSelector,
  mapLayerSelector,
  mapLayerSettingsSelector,
  operationalLayersSelector,
  savedGeofenceSelector,
  savingGeofenceSelector,
  scDevicesLabelContentSelector,
  searchResultSelector,
  selectedGeoFencesSelector,
  showHistoryRefreshMessageSelector,
  trafficOpacitySelector,
  trafficSubLayersSelector,
  weatherOpacitySelector
} from '@/store/selectors/tracking-selectors';

/**
 * The useLayerChooserProps hook is used for sharing props to Layer Chooser Components.
 * 
 * ```typescript
 * 
 *   import useLayerChooserProps from './hooks/useLayerChooserProps';
 * 
 *   const layerChooserProps = useLayerChooserProps();
 * 
 *   // layerChooserProps
 * 
 * ```
 */
export const useLayerChooserProps = () => {
  const dispatch                      = useDispatch();
  const trackingMapLayersRef          = useRef<TrackingMapLayer>();
  const currentLocationRef            = useRef<LatLong>(null);
  const searchedResultRef             = useRef(null);
  const geofenceStatus                = useSelector(geofenceStatusSelector);
  const geoFencePanelOpen             = useSelector(geoFencePanelOpenSelector);
  const user                          = useSelector(userSelector);
  const layers                        = useSelector(mapLayerSelector);
  const operationalLayers             = useSelector(operationalLayersSelector);
  const scDevicesLabelContent         = useSelector(scDevicesLabelContentSelector);
  const isScDevicesLabelActive        = useSelector(isScDevicesLabelActiveSelector);
  const isClusteringActive            = useSelector(isClusteringActiveSelector);
  const isClusteringV2Active          = useSelector(isClusteringV2ActiveSelector);
  const isLabellingActive             = useSelector(isLabellingActiveSelector);
  const labelContent                  = useSelector(assetLabelContentSelector);
  const historyReportType             = useSelector(historyReportTypeSelector);
  const historyInputEvents            = useSelector(historyInputEventsSelector);
  const showHistoryRefresh            = useSelector(showHistoryRefreshMessageSelector);
  const trafficOpacity                = useSelector(trafficOpacitySelector);
  const trafficSubLayers              = useSelector(trafficSubLayersSelector);
  const weatherOpacity                = useSelector(weatherOpacitySelector);
  const geofenceOpacity               = useSelector(geofenceOpacitySelector);
  const geofenceBorderOnly            = useSelector(geofenceBorderOnlySelector);
  const geofenceLabelState            = useSelector(geofenceLabelSelector);
  const assetsHistoryLoading          = useSelector(assetsHistoryLoadingSelector);
  const historyIntervalInHours        = useSelector(historyIntervalInHoursSelector);
  const islayerVisibilityUpdate       = useSelector(islayerVisibilitySelector);
  const savedGeofence                 = useSelector(savedGeofenceSelector);
  const selectedGeoFences             = useSelector(selectedGeoFencesSelector);
  const savingGeofence                = useSelector(savingGeofenceSelector);
  const mapLayerSettings              = useSelector(mapLayerSettingsSelector);
  const findLabel                     = useSelector(findNearbySelector);
  const searchResult                  = useSelector(searchResultSelector);
  const accountMap                    = useSelector(globalAccountMapSelector);
  const globalGroup                   = useSelector(globalGroupSelector);
  const isLiveHistoryFetching         = useSelector(liveHistoryIsFetchingSelector);
  const isLiveHistoryUIFetching       = useSelector(liveHistoryIsUIFetchingSelector);
  const isLiveHistoryActive           = useSelector(liveHistoryActiveSelector);
  const isHistoryActive               = useSelector(historyActiveSelector);
  const isHideNonSelectedAssetsActive = useSelector(liveHistoryHideNonSelectedAssetsActive);
  const liveHistoryInitialAssets      = useSelector(liveHistoryInitialAssetsSelector);
  const boundaryMode                  = useSelector(boundaryModeSelector);

  const [isSCDeviceToggleChanged, setSCDeviceToggleChanged] = useState(false);
  const hasSCITrackingAccess = useMemo(() => Features.hasSCIAccess(), [Features.hasSCIAccess()]);

  // invoked after the tracking map layer configurations have changes, at which
  // point the current layer state is persisted to the user's preferences ...
  const savePreferences = debounce(() => {
    const previous = UserPreferences.get(Preference.trackingMapLayers);
    const current  = trackingMapLayersRef.current;
    UserPreferences.set(Preference.trackingMapLayers, current);
    if (notDeepEqual(previous, current)) {
      UserPreferences.save();
    }
  }, 1000, {maxWait: 0});

  useEffect(() => {
    const UserPreferencesLayers = [
      TRAFFIC_LAYER,
      WEATHER_LAYER,
      GEOFENCE_LAYER,
      SC_DEVICE_LAYER
    ];
    const enabledLayers = layers.filter(item => UserPreferencesLayers.includes(item));
    trackingMapLayersRef.current = {
      labelContent,
      enabledLayers,
      weatherOpacity,
      trafficOpacity,
      geofenceOpacity,
      trafficSubLayers,
      geofenceBorderOnly,
      geofenceLabelState,
      isClusteringActive,
      scDevicesLabelContent,
      isScDevicesLabelActive,
      ...(isSCDeviceToggleChanged && {isSCDeviceAutoInitialized: true})
    };
    savePreferences();
  }, [
    layers,
    labelContent,
    trafficOpacity,
    weatherOpacity,
    geofenceOpacity,
    trafficSubLayers,
    isClusteringActive,
    geofenceBorderOnly,
    geofenceLabelState,
    scDevicesLabelContent,
    isScDevicesLabelActive,
    isSCDeviceToggleChanged
  ]);

  // this hook is mounted fresh every time `MapWidgetContainer` remounts -
  // which happens on every Live/History page swap, since it's gated behind
  // `renderMapWidgets` rather than persisting the way its own map host does.
  // Every fresh mount re-runs this effect regardless of whether `user`
  // actually changed, since there is no prior render on a new instance to
  // compare against. Re-applying the saved layer preference at that point
  // would silently overwrite whatever Timeline/History had just set - the
  // Fleet layer back on, the report's own layer un-set - so it is skipped
  // while a report is actually running ...
  useEffect(() => {
    if (!isLiveHistoryActive && !isHistoryActive) {
      const trackingMapLayers = UserPreferences.get(Preference.trackingMapLayers);
      trackingMapLayersRef.current = trackingMapLayers;
      if (trackingMapLayers) {
        dispatch(updateLayers(trackingMapLayers, layers) as any);
        updateMapLayers({
          savedLayers : trackingMapLayers
        });
        if (trackingMapLayers?.isSCDeviceAutoInitialized) {
          setSCDeviceToggleChanged(true);
        }
      }
    }
  }, [user, isLiveHistoryActive, isHistoryActive]);

  const updateMapLayers = useCallback(({
    savedLayers,
    savedOperationalLayers = null,
    isAppendLayers = true
  }) => {
    if (savedLayers && layers) {
      dispatch(updateLayers(savedLayers, isAppendLayers ? layers : []) as any);
    }
    if (savedOperationalLayers) {
      dispatch(setSavedOperationalLayers(savedOperationalLayers));
    }
  }, [layers]);

  const getHVSAssets = useCallback((
    hours: number = 2,
    force: boolean = false,
    isUpdate: boolean = false
  ) => {
    dispatch(loadAssetHistory(hours, isUpdate, force) as any);
  }, []);

  const setSCDeviceAutoInitialized = useCallback(debounce(() => {
    if (!isSCDeviceToggleChanged) {
      setSCDeviceToggleChanged(true);
    }
  }, 250), [isSCDeviceToggleChanged]);

  const onFleetLayerPersistenceActive = useCallback(status =>
    dispatch(setFleetLayerPersistenceActive(status)), []);

  const setLayers = useCallback(function(layers) {
    dispatch(setTrackingMapLayers(layers));
  }, []);

  const setTrafficSubLayers = useCallback(function(trafficSubLayers) {
    dispatch(setTrafficLayerSubLayers(trafficSubLayers));
  }, []);

  const setTrafficOpacity = useCallback(debounce(function(trafficOpacity) {
    dispatch(setTrafficLayerOpacity(trafficOpacity));
  }, 250), []);

  const setWeatherOpacity = useCallback(debounce(function(weatherOpacity) {
    dispatch(setWeatherLayerOpacity(weatherOpacity));
  }, 250), []);

  const setGeofenceOpacity = useCallback(debounce(function(geofenceOpacity) {
    dispatch(setGeofenceLayerOpacity(geofenceOpacity));
  }, 250), []);

  const mapControlsToggle = useCallback(function(mapControlState) {
    dispatch(toggleMapControls(mapControlState));
  }, []);

  const setGoefenceBorderOnly = useCallback(function(borderOnly) {
    dispatch(setGeofenceBorderOnly(borderOnly));
  }, []);

  const setGoefenceLabel = useCallback(function(labelState) {
    dispatch(setGeofenceLabelState(labelState));
  }, []);

  const toggleAssetsClustering = useCallback(function(status: boolean, fromMap: boolean = false) {
    dispatch(toggleAssetClustering(status, fromMap));
  }, []);

  const toggleAssetsLabelling = useCallback(function(status) {
    dispatch(toggleAssetLabelling(status));
  }, []);
    
  const onChangeLabelContent = useCallback((content) => {
    dispatch(onChangeAssetContent(content));
  }, []);
    
  const toggleDevicesLabelling = useCallback(function(status) {
    dispatch(toggleDeviceLabelling(status));
  }, []);
    
  const onChangeDevicesLabelContent = useCallback((content) => {
    dispatch(onChangeDeviceLabelContent(content));
  }, []);

  const handleGeoFencePanel = useCallback(function(open: boolean) {
    dispatch(setGeoFencePanelOpen(open));
  }, []);

  const setLayersOpacity = useCallback(debounce(function(updated) {
    dispatch(setOperationalLayersOpacity(updated));
  }, 250), []);

  const setLayerVisibility = useCallback(function(updated) {
    dispatch(setOperationalLayerVisibility(updated));
  }, []);

  const setSublayerVisibility = useCallback(debounce(function(updated) {
    dispatch(setOperationalSublayerVisibility(updated));
  }, 250), []);

  const setAssetsHistoryLive = useCallback((status: boolean) => {
    dispatch(setAssetsHistoryUpdatesLive(status));
  }, []);

  const setHistoryInterval = useCallback(function(hours: number) {
    dispatch(setAssetHistoryInterval(hours));
  }, []);

  const resetHistoryReport = useCallback(() => {
    dispatch(showHistoryRefreshMessage(false));
    dispatch(resetAssetHistory());
  }, [dispatch]);

  const setHistoryReportType = useCallback(function(reportType: HistoryReportTypeEnum) {
    dispatch(setAssetHistoryReportType(reportType));
  }, []);

  const setHistoryInputEvents = useCallback(function(inputEvents: HistoryInputEvents[]) {
    dispatch(setAssetHistoryInputEvents(inputEvents));
  }, []);

  const onCloseSearchPopup = useCallback(() => {
    dispatch(clearSelectedTrackingSearchResult() as any);
  }, []);

  const setSelectedTrackingSearch = (result) => {
    dispatch(setSelectedTrackingSearchResult(result) as any);
  };

  const hideNonSelectedAssetsActive = useMemo(() =>
    isHideNonSelectedAssetsActive && isLiveHistoryActive && (
      liveHistoryInitialAssets?.length > 0 || boundaryMode === BoundaryMode.Vicinity
    )
  , [isHideNonSelectedAssetsActive, liveHistoryInitialAssets, isLiveHistoryActive, boundaryMode]);

  const layerChooserProps = useMemo(() => {
    return {
      selectedLayers                           : layers,
      onLayersChange                           : setLayers,
      setTrafficSubLayers                      : setTrafficSubLayers,
      geofenceStatus                           : geofenceStatus,
      trafficLayerURL                          : get('trafficLayerURL'),
      weatherLayerURL                          : get('weatherLayerURL'),
      onTrafficOpacityChange                   : setTrafficOpacity,
      onWeatherOpacityChange                   : setWeatherOpacity,
      onGeofenceOpacityChange                  : setGeofenceOpacity,
      onMapControlsToggle                      : mapControlsToggle,
      onGeofenceBorderOnlyChange               : setGoefenceBorderOnly,
      onGeofenceLabelChange                    : setGoefenceLabel,
      onToggleClustering                       : toggleAssetsClustering,
      onToggleLabelling                        : toggleAssetsLabelling,
      onToggleDeviceLabelling                  : toggleDevicesLabelling,
      geoFencePanelOpen                        : geoFencePanelOpen,
      setGeoFencePanelOpen                     : handleGeoFencePanel,
      onLayersOpacityChange                    : setLayersOpacity,
      onLayerVisibilityChange                  : setLayerVisibility,
      onSublayerVisibilityChange               : setSublayerVisibility,
      isSCDeviceAutoInitialized                : isSCDeviceToggleChanged,
      loadAssetHistory                         : getHVSAssets,
      setAssetsHistoryLive                     : setAssetsHistoryLive,
      setHistoryInterval                       : setHistoryInterval,
      isClusteringActive                       : isClusteringV2Active,
      isLiveHistoryHideNonSelectedAssetsActive : hideNonSelectedAssetsActive,
      setFleetLayerPersistenceActive           : onFleetLayerPersistenceActive,
      user,
      findLabel,
      accountMap,
      globalGroup,
      labelContent,
      searchResult,
      savedGeofence,
      trafficOpacity,
      weatherOpacity,
      savingGeofence,
      updateMapLayers,
      geofenceOpacity,
      trafficSubLayers,
      mapLayerSettings,
      isLabellingActive,
      historyReportType,
      selectedGeoFences,
      operationalLayers,
      searchedResultRef,
      currentLocationRef,
      historyInputEvents,
      showHistoryRefresh,
      resetHistoryReport,
      geofenceBorderOnly,
      geofenceLabelState,
      onCloseSearchPopup,
      isLiveHistoryActive,
      isClusteringV2Active,
      hasSCITrackingAccess,
      onChangeLabelContent,
      setHistoryReportType,
      assetsHistoryLoading,
      isLiveHistoryFetching,
      scDevicesLabelContent,
      setHistoryInputEvents,
      isScDevicesLabelActive,
      historyIntervalInHours,
      isLiveHistoryUIFetching,
      islayerVisibilityUpdate,
      setSelectedTrackingSearch,
      setSCDeviceAutoInitialized,
      onChangeDevicesLabelContent,
      onFleetLayerPersistenceActive
    };
  }, [
    user,
    layers,
    findLabel,
    setLayers,
    accountMap,
    globalGroup,
    labelContent,
    searchResult,
    getHVSAssets,
    savedGeofence,
    weatherOpacity,
    trafficOpacity,
    geofenceStatus,
    savingGeofence,
    geofenceOpacity,
    updateMapLayers,
    setGoefenceLabel,
    mapLayerSettings,
    trafficSubLayers,
    setLayersOpacity,
    selectedGeoFences,
    isLabellingActive,
    geoFencePanelOpen,
    mapControlsToggle,
    setTrafficOpacity,
    setWeatherOpacity,
    historyReportType,
    operationalLayers,
    searchedResultRef,
    currentLocationRef,
    geofenceLabelState,
    historyInputEvents,
    showHistoryRefresh,
    resetHistoryReport,
    setHistoryInterval,
    setLayerVisibility,
    setGeofenceOpacity,
    geofenceBorderOnly,
    onCloseSearchPopup,
    handleGeoFencePanel,
    setTrafficSubLayers,
    isLiveHistoryActive,
    assetsHistoryLoading,
    setHistoryReportType,
    onChangeLabelContent,
    hasSCITrackingAccess,
    setAssetsHistoryLive,
    setHistoryInputEvents,
    toggleAssetsLabelling,
    setSublayerVisibility,
    isLiveHistoryFetching,
    scDevicesLabelContent,
    setGoefenceBorderOnly,
    toggleDevicesLabelling,
    historyIntervalInHours,
    toggleAssetsClustering,
    isScDevicesLabelActive,
    isLiveHistoryUIFetching,
    isSCDeviceToggleChanged,
    islayerVisibilityUpdate,
    setSelectedTrackingSearch,
    setSCDeviceAutoInitialized,
    hideNonSelectedAssetsActive,
    onChangeDevicesLabelContent
  ]);
  
  return layerChooserProps;
};

export default useLayerChooserProps;
